/**
 * OrderNotesPanel — Sage order notes on the order detail screen.
 */

import { useState, useEffect, useCallback } from 'react';
import { portalOrderService } from '../../services/portalOrders';
import type { SageOrderNotes } from '../../services/portalOrders';
import styles from './OrderNotesPanel.module.css';

interface OrderNotesPanelProps {
  orderId: string;
  /** Hide the CS-note composer and archive actions (orders:read viewers). */
  readOnly?: boolean;
}

const OrderNotesPanel: React.FC<OrderNotesPanelProps> = ({ orderId, readOnly = false }) => {
  const [notes, setNotes] = useState<SageOrderNotes | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    kind: 'success' | 'error';
    text: string;
  } | null>(null);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFeedback(null);
    try {
      const data = await portalOrderService.getSageNotes(orderId);
      setNotes(data);
      setDraft('');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load order notes';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const canSubmit = notes != null && draft.trim().length > 0 && !saving;

  const handleSave = useCallback(async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const resp = await portalOrderService.saveCsNotes(orderId, draft);
      setFeedback({
        kind: resp.succeeded ? 'success' : 'error',
        text: resp.message,
      });
      setNotes(resp.notes);
      if (resp.succeeded) setDraft('');
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to queue CS note';
      setFeedback({ kind: 'error', text: message });
    } finally {
      setSaving(false);
    }
  }, [orderId, draft]);

  const handleArchive = useCallback(
    async (entryId: string) => {
      setArchivingId(entryId);
      setFeedback(null);
      try {
        const resp = await portalOrderService.archiveCsNote(orderId, entryId);
        setFeedback({
          kind: resp.succeeded ? 'success' : 'error',
          text: resp.message,
        });
        setNotes(resp.notes);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Failed to archive CS note';
        setFeedback({ kind: 'error', text: message });
      } finally {
        setArchivingId(null);
      }
    },
    [orderId],
  );

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>Order Notes</span>
        <span className={styles.subtitle}>Sage sales-order notes</span>
      </div>

      {loading && <div className={styles.loading}>Loading order notes…</div>}

      {!loading && error && (
        <div className={styles.errorBox} role="alert">
          {error}
        </div>
      )}

      {!loading && !error && notes && (
        <div className={styles.body}>
          <div className={`${styles.note} ${styles.noteReadOnly}`}>
            <div className={styles.noteLabel}>
              Production Notes
              <span className={styles.noteLabelHint}>
                read-only · production team
              </span>
            </div>
            {notes.order_notes ? (
              <div className={styles.readOnlyText}>{notes.order_notes}</div>
            ) : (
              <div className={styles.emptyText}>No production notes.</div>
            )}
          </div>

          <div className={`${styles.note} ${styles.noteEditable}`}>
            <div className={styles.noteLabel}>
              CS Notes
              <span className={styles.noteLabelHint}>
                append-only · customer service
              </span>
            </div>

            {notes.available && notes.order_cs_notes_writable ? (
              <>
                <div className={styles.rowList}>
                  {notes.order_cs_note_rows.length > 0 ? (
                    notes.order_cs_note_rows.map((row, index) => (
                      <div
                        className={styles.noteRow}
                        key={row.id ?? `${row.date_label ?? 'sage'}-${index}`}
                      >
                        <div className={styles.rowMeta}>
                          <span>{row.date_label ?? 'Undated'}</span>
                          <span className={styles.statusBadge}>{row.status}</span>
                        </div>
                        <div className={styles.rowText}>{row.text}</div>
                        {row.error && (
                          <div className={styles.rowError}>{row.error}</div>
                        )}
                        {!readOnly && row.id && row.can_archive && (
                          <button
                            type="button"
                            className={styles.archiveBtn}
                            onClick={() => handleArchive(row.id as string)}
                            disabled={archivingId === row.id}
                          >
                            {archivingId === row.id ? 'Archiving…' : 'Archive locally'}
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className={styles.emptyText}>No CS notes.</div>
                  )}
                </div>

                {!readOnly && (
                  <>
                    <textarea
                      className={styles.textarea}
                      aria-label="New customer service note"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      disabled={saving}
                      rows={4}
                      placeholder="Add a dated customer-service note…"
                    />
                    <div className={styles.actions}>
                      {draft.trim().length > 0 && !saving && (
                        <span className={styles.unsaved}>Not queued yet</span>
                      )}
                      <button
                        type="button"
                        className={styles.saveBtn}
                        onClick={handleSave}
                        disabled={!canSubmit}
                      >
                        {saving ? 'Queueing…' : 'Queue CS Note'}
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className={styles.unavailableBox}>
                {notes.unavailable_reason ??
                  'CS notes are unavailable for this order.'}
              </div>
            )}

            {feedback && (
              <div
                className={
                  feedback.kind === 'success'
                    ? styles.feedbackSuccess
                    : styles.feedbackError
                }
                role={feedback.kind === 'error' ? 'alert' : 'status'}
              >
                {feedback.text}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderNotesPanel;
